import type { TriageStatus, ReportDesignerReview } from "@iamu/shared";
import type { LiveFinding, LivePhase } from "@/hooks/use-walkthrough-stream";
import type { TargetBox } from "@/components/walkthrough/screenshot-types";

export type Severity = "critical" | "major" | "minor" | "nit";
export type FindingType =
  | "helps"
  | "hinders"
  | "missing"
  | "irrelevant"
  | "incorrect";

export interface LiveWalkthroughData {
  id: string;
  personaId: string;
  scenarioId: string;
  mode: string;
  status: string;
  errorMessage?: string | null;
  targetUrl: string | null;
  reportMarkdown?: string | null;
  designerReview?: ReportDesignerReview | null;
  steps: Array<{
    id: string;
    stepNumber: number;
    title: string;
    personaReasoning: string | null;
    grounding: string | null;
    screenshotPath: string | null;
  }>;
  findings: Array<{
    id: string;
    walkthroughStepId: string | null;
    type: string;
    severity: string | null;
    description: string;
    recommendation: string | null;
    prototypeSection: string | null;
    triageStatus?: TriageStatus | null;
    triageNote?: string | null;
    source?: "persona" | "designer";
    lens?: string | null;
    targetBox?: TargetBox | null;
    citations: Array<{
      sourceType: string;
      sourceId: string;
      characterization: string | null;
    }>;
  }>;
  artifacts: Array<{
    id: string;
    type: string;
    content: string | null;
    filePath: string | null;
  }>;
  /** Set while a local run is followed live; cleared when it syncs. */
  liveState?: { phase: LivePhase; findings: LiveFinding[] } | null;
}

export interface UnifiedLiveFinding {
  id: string;
  title: string;
  type: FindingType;
  severity: Severity;
  stepNumber: number | null;
  description: string;
  recommendation?: string | null;
  uiFact?: string | null;
  consequence?: string | null;
  isPreliminary: boolean;
  triageStatus?: TriageStatus | null;
  triageNote?: string | null;
  source?: "persona" | "designer";
  targetBox?: TargetBox | null;
}

export const PHASE_LABELS: Record<Exclude<LivePhase, "walking">, string> = {
  completing: "Writing the report…",
  matching: "Matching findings to earlier runs…",
  judging: "Judging the human tests…",
  publishing: "Syncing the finished run…",
};

export type LiveRightTab = "timeline" | "findings" | "designer";
